import * as React from 'react';
/* eslint-disable @typescript-eslint/no-explicit-any */ import { $as, $extends, $render } from './constants';
import { continuationIntent } from './continuation';
import { getIntents } from '../../intent';
import { createElement, getViewConfig, createPropsFacade } from './props';
import { runProviders } from './provider';
import { runRenderIntents } from './render';
import { createSlots } from './slots';
import { applyDefaults } from './setDefaults';
import { runPropTransforms } from './transformProps';
function ViewImpl(facade) {
    const props = facade;
    // Call render intents
    runRenderIntents(props);
    // Create the React element
    const element = createElement(props);
    // Wrap the React element with context providers (using intents)
    return runProviders(props, element);
}
function createViewComponent(render, tag, extend, css, intents) {
    function Render(props) {
        const continuationIntents = getIntents(continuationIntent, getViewConfig(props));
        const continuation = continuationIntents === null || continuationIntents === void 0 ? void 0 : continuationIntents.pop();
        if (continuation) {
            return continuation(React.createElement(Render, props));
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return render(props, extend || BaseView);
    }
    const component = React.forwardRef(// @ts-expect-error Type fails in React 18
    (props, forwardedRef)=>{
        // This cache allows us to maintain stable identities of computed values
        // across renders
        const cache = React.useRef({});
        const facade = createPropsFacade(props, cache.current, tag, css, forwardedRef, intents);
        runPropTransforms(facade);
        applyDefaults(facade);
        return Render(facade);
    });
    component[$as] = tag;
    component[$extends] = extend;
    component[$render] = render;
    component.as = function as(newTag) {
        return createViewComponent(render, newTag, component, css, intents);
    };
    component.styles = function styles(css) {
        return createViewComponent(render, tag, component, css, intents);
    };
    component.create = function create(newRender, intents) {
        return createViewComponent(newRender, tag, component, css, intents);
    };
    component.extend = function extendView(renderExtendingView, additionalIntents = []) {
        const newComponent = createViewComponent((props)=>{
            // Call the extending render method with the current props and the
            // extended view.
            const element = renderExtendingView(props, component);
            if (process.env.NODE_ENV !== 'production') {
                if (element.type !== component) {
                    throw new Error('View.extend must always return <View> as the outermost element');
                }
            }
            // Create a new cache object for the extended view and attach it to the
            // existing view config to ensure they both remain referentially stable.
            const config = getViewConfig(props);
            if (!config.cache.extendedViewCache) {
                config.cache.extendedViewCache = {};
            }
            const cache = config.cache.extendedViewCache;
            // Create a new props facade for the extended view using the returned
            // element’s props and ref, as well as the new cache.
            const facade = createPropsFacade(element.props, cache, tag, css, element.ref);
            runPropTransforms(facade);
            applyDefaults(facade);
            // Immediately render the extended view. This collapses the extending
            // view and extended view into a single React component.
            return Render(facade);
        }, tag, extend, css, intents ? intents.concat(additionalIntents) : additionalIntents);
        if (renderExtendingView.name) {
            newComponent.displayName = renderExtendingView.name;
        }
        return newComponent;
    };
    component.displayName = 'view.unknown';
    if (render.name && render !== ViewImpl) {
        component.displayName = render.name;
    } else if (typeof tag === 'string') {
        component.displayName = `view.${tag}`;
    } else if (typeof tag === 'object' || typeof tag === 'function') {
        component.displayName = `view.${tag.displayName || tag.name || 'unknown'}`;
    }
    Render.displayName = `→ ${component.displayName}`;
    return component;
}
/** @deprecated */ export const createView = createViewComponent;
const BaseView = createViewComponent(ViewImpl);
BaseView.displayName = 'View';
/** @deprecated */ export const View = BaseView;
/**
 * Creates a `view` API bound to the provided `css` serializer. The computed
 * properties type must be supplied manually, like so:
 *
 *   createIntrinsicViews<typeof css.ComputedProperties>(css);
 */ export function createIntrinsicViews(// We don’t infer ComputedProperties from this serializer because it causes a
// substantial TypeScript performance hit whenever the method is invoked.
css) {
    return new Proxy({
        create: createViewComponent,
        slots: createSlots
    }, {
        get (target, tag) {
            const cached = target[tag];
            if (cached) {
                return cached;
            }
            const component = createViewComponent(ViewImpl, tag, undefined, css);
            target[tag] = component;
            return component;
        }
    });
}
export const view = createIntrinsicViews();

//# sourceMappingURL=view.js.map